// ScanHistory.jsx — Applied history for the Existing Attachment Field Scan
// tool: a filterable log of every write to FMX, expandable to the field
// level (previous → applied), revertable per record. Rows come from
// equipment_scan_writes (migration 029).

import { Fragment, useMemo, useState } from 'react';
import { T } from '../../theme';
import { Button, IconButton, Badge } from '../fmxkit';
import { ConfTag, ScanFilterChip, fmxEquipmentUrl } from './scanShared';

function formatWhen(ts) {
  if (!ts) return '—';
  const d = new Date(ts);
  const now = new Date();
  const time = d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
  const sameDay = (a, b) => a.toDateString() === b.toDateString();
  if (sameDay(d, now)) return `Today ${time}`;
  const yesterday = new Date(now); yesterday.setDate(now.getDate() - 1);
  if (sameDay(d, yesterday)) return `Yesterday ${time}`;
  return `${d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })} ${time}`;
}

function exportCsv(writes) {
  const esc = (v) => { const s = v == null ? '' : String(v); return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s; };
  const lines = [['Equipment ID', 'Tag', 'Type', 'Building', 'Batch', 'Applied by', 'Applied at', 'Field', 'Previous value', 'Applied value', 'Confidence', 'Edited', 'Source attachment', 'Reverted'].join(',')];
  for (const r of writes) {
    for (const w of r.writes || []) {
      lines.push([
        r.equipment_id, r.equipment_tag, r.equipment_type_name, r.building_name,
        r.batch_num != null ? `#${r.batch_num}` : '', r.applied_by_name, r.created_at,
        w.label, w.old, w.value, w.edited ? '' : w.confidence, w.edited ? 'yes' : '', w.sourceFilename,
        r.reverted ? 'yes' : '',
      ].map(esc).join(','));
    }
  }
  const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = 'attachment-field-scan-history.csv';
  a.click();
  URL.revokeObjectURL(url);
}

export default function ScanHistory({ project, projectName, writes, onBack, onRevert }) {
  const [typeFilter, setTypeFilter] = useState(null);
  const [bldFilter, setBldFilter] = useState(null);
  const [batchFilter, setBatchFilter] = useState(null);
  const [q, setQ] = useState('');
  const [open, setOpen] = useState(writes[0]?.id ?? null);
  const [reverting, setReverting] = useState(null); // write id in flight
  const [revertError, setRevertError] = useState(null);

  const batchLabel = (r) => (r.batch_num != null ? `#${r.batch_num}` : '—');
  const matches = (r) =>
    (!typeFilter || r.equipment_type_name === typeFilter) &&
    (!bldFilter || r.building_name === bldFilter) &&
    (!batchFilter || batchLabel(r) === batchFilter) &&
    (!q || (r.equipment_tag || r.equipment_id || '').toLowerCase().includes(q.toLowerCase()));
  const rows = writes.filter(matches);
  const filtersOn = !!(typeFilter || bldFilter || batchFilter || q);

  const countBy = (fn) => {
    const m = {};
    writes.forEach(r => { const v = fn(r); if (v) m[v] = (m[v] || 0) + 1; });
    return Object.keys(m).sort().map(name => ({ name, count: m[name] }));
  };

  const totals = useMemo(() => ({
    records: writes.length,
    values: writes.reduce((n, r) => n + (r.writes?.length || 0), 0),
    batches: new Set(writes.map(r => r.batch_num).filter(n => n != null)).size,
  }), [writes]);

  const revert = async (r) => {
    setReverting(r.id);
    setRevertError(null);
    try {
      await onRevert(r);
    } catch (e) {
      setRevertError(e?.message || 'Failed to revert');
    } finally {
      setReverting(null);
    }
  };

  const hs = (w) => ({ textAlign: 'left', font: `700 12px/16px ${T.fontSans}`, color: T.inkMuted, textTransform: 'uppercase', letterSpacing: '0.04em', padding: '9px 14px', background: T.bgMuted, borderBottom: `1px solid ${T.border}`, width: w, whiteSpace: 'nowrap' });
  const cs = { padding: '9px 14px', borderBottom: `1px solid ${T.borderSubtle}`, font: `400 13px/18px ${T.fontSans}`, color: T.ink, verticalAlign: 'middle' };

  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      {/* header */}
      <div style={{ padding: '14px 24px 12px', borderBottom: `1px solid ${T.borderSubtle}`, background: T.white, display: 'flex', alignItems: 'center', gap: 12 }}>
        <IconButton icon="arrow-left" title="Back to review queue" onClick={onBack} style={{ marginLeft: -6 }} />
        <div style={{ minWidth: 0 }}>
          <div style={{ font: `400 12.5px/16px ${T.fontSans}`, color: T.inkMuted, marginBottom: 2 }}>
            Projects <i className="fa-solid fa-chevron-right" style={{ fontSize: 8 }} /> {projectName} <i className="fa-solid fa-chevron-right" style={{ fontSize: 8 }} /> Existing Attachment Field Scan
          </div>
          <h2 style={{ font: `700 19px/25px ${T.fontSans}`, color: T.ink, margin: 0, letterSpacing: '-0.01em', whiteSpace: 'nowrap' }}>Applied history</h2>
        </div>
        <span style={{ flex: 1 }} />
        <span style={{ font: `400 13px/18px ${T.fontSans}`, color: T.inkMuted, whiteSpace: 'nowrap' }}>
          {totals.records} records · {totals.values} values written · {totals.batches} batch{totals.batches === 1 ? '' : 'es'}
        </span>
        <Button variant="neutral" icon="file-export" size="sm" disabled={!writes.length} onClick={() => exportCsv(writes)}>Export CSV</Button>
      </div>

      {/* filter bar */}
      <div style={{ padding: '10px 24px', borderBottom: `1px solid ${T.borderSubtle}`, background: T.white, display: 'flex', alignItems: 'center', gap: 8 }}>
        <div style={{ position: 'relative', width: 230 }}>
          <i className="fa-solid fa-magnifying-glass" style={{ position: 'absolute', left: 9, top: 8, color: T.inkSoft, fontSize: 11 }} />
          <input value={q} onChange={e => setQ(e.target.value)} placeholder="Search equipment tag…"
            style={{ width: '100%', height: 27, padding: '0 9px 0 26px', border: `1px solid ${T.border}`, borderRadius: T.radiusPill, font: `400 12.5px/18px ${T.fontSans}`, outline: 'none', background: T.bg, boxSizing: 'border-box' }} />
        </div>
        <div style={{ width: 130 }}><ScanFilterChip label="Type" value={typeFilter} options={countBy(r => r.equipment_type_name)} onChange={setTypeFilter} /></div>
        <div style={{ width: 150 }}><ScanFilterChip label="Building" value={bldFilter} options={countBy(r => r.building_name)} onChange={setBldFilter} /></div>
        <div style={{ width: 110 }}><ScanFilterChip label="Batch" value={batchFilter} options={countBy(batchLabel)} onChange={setBatchFilter} /></div>
        {filtersOn && (
          <span onClick={() => { setTypeFilter(null); setBldFilter(null); setBatchFilter(null); setQ(''); }}
            style={{ font: `600 12px/16px ${T.fontSans}`, color: T.brand, cursor: 'pointer', whiteSpace: 'nowrap' }}>
            <i className="fa-solid fa-filter-circle-xmark" style={{ fontSize: 10, marginRight: 4 }} />Clear filters
          </span>
        )}
        <span style={{ marginLeft: 'auto', font: `400 12px/16px ${T.fontSans}`, color: T.inkSoft }}>{rows.length} of {writes.length} records</span>
      </div>

      {/* table */}
      <div style={{ flex: 1, overflow: 'auto', background: T.bg, padding: 20 }}>
        {revertError && (
          <div style={{ font: `400 12.5px/17px ${T.fontSans}`, color: T.red500, background: T.tint.error, border: `1px solid ${T.badge.red}`, borderRadius: T.radiusBtn, padding: '8px 12px', marginBottom: 10 }}>
            {revertError}
          </div>
        )}
        <div style={{ background: T.white, border: `1px solid ${T.borderSubtle}`, borderRadius: T.radiusTable, overflow: 'hidden', boxShadow: T.shadowSm }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={hs(30)}></th>
                <th style={hs()}>Equipment</th>
                <th style={hs()}>Building</th>
                <th style={hs(110)}>Values</th>
                <th style={hs(80)}>Batch</th>
                <th style={hs(130)}>Applied by</th>
                <th style={hs(150)}>Applied</th>
                <th style={hs(150)}></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const isOpen = open === r.id;
                const isRev = !!r.reverted;
                const edits = (r.writes || []).filter(w => w.edited).length;
                return (
                  <Fragment key={r.id}>
                    <tr onClick={() => setOpen(isOpen ? null : r.id)} style={{ cursor: 'pointer', background: isOpen ? T.blue50 : T.white, opacity: isRev ? 0.6 : 1 }}>
                      <td style={{ ...cs, paddingRight: 0 }}><i className={`fa-solid fa-chevron-${isOpen ? 'down' : 'right'}`} style={{ fontSize: 10, color: T.inkMuted }} /></td>
                      <td style={cs}>
                        <b style={{ fontWeight: 600 }}>{r.equipment_tag || `#${r.equipment_id}`}</b>
                        {r.equipment_type_name && <span style={{ color: T.inkSoft }}> · {r.equipment_type_name}</span>}
                      </td>
                      <td style={cs}>{r.building_name || '—'}</td>
                      <td style={cs}>{(r.writes || []).length}{edits > 0 && <span style={{ color: T.inkSoft }}> · {edits} edited</span>}</td>
                      <td style={cs}>{batchLabel(r)}</td>
                      <td style={cs}>{r.applied_by_name || '—'}</td>
                      <td style={{ ...cs, whiteSpace: 'nowrap' }}>{formatWhen(r.created_at)}</td>
                      <td style={{ ...cs, textAlign: 'right', whiteSpace: 'nowrap' }} onClick={e => e.stopPropagation()}>
                        <span style={{ display: 'inline-flex', gap: 4, alignItems: 'center', justifyContent: 'flex-end' }}>
                          {isRev ? (
                            <Badge color="orange" icon="rotate-left">Reverted</Badge>
                          ) : (
                            <span onClick={() => reverting == null && revert(r)} title="Restore previous values in FMX"
                              style={{ font: `600 12px/16px ${T.fontSans}`, color: T.inkMuted, cursor: 'pointer' }}>
                              <i className={`fa-solid fa-${reverting === r.id ? 'spinner fa-spin' : 'rotate-left'}`} style={{ fontSize: 10, marginRight: 4 }} />
                              {reverting === r.id ? 'Reverting…' : 'Revert'}
                            </span>
                          )}
                          <a href={fmxEquipmentUrl(project?.fmx_site_url, r.equipment_id)} target="_blank" rel="noopener noreferrer" title="View in FMX"
                            style={{ display: 'inline-flex', alignItems: 'center', gap: 4, font: `600 12px/16px ${T.fontSans}`, color: T.brand, textDecoration: 'none', marginLeft: 8 }}>
                            View<i className="fa-solid fa-up-right-from-square" style={{ fontSize: 9 }} />
                          </a>
                        </span>
                      </td>
                    </tr>
                    {isOpen && (
                      <tr>
                        <td colSpan={8} style={{ padding: 0, borderBottom: `1px solid ${T.borderSubtle}`, background: T.bgMuted }}>
                          <div style={{ padding: '10px 14px 12px 44px' }}>
                            <div style={{ font: `700 11px/15px ${T.fontSans}`, color: T.inkSoft, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 }}>Values written to FMX</div>
                            <div style={{ background: T.white, border: `1px solid ${T.borderSubtle}`, borderRadius: T.radiusBtn, overflow: 'hidden', maxWidth: 760 }}>
                              {(r.writes || []).map((w, i) => (
                                <div key={`${w.label}-${i}`} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '7px 12px', borderBottom: i < r.writes.length - 1 ? `1px solid ${T.borderSubtle}` : 'none' }}>
                                  <span style={{ font: `500 12.5px/17px ${T.fontSans}`, color: T.inkMuted, width: 170, flexShrink: 0 }}>{w.label}</span>
                                  <span style={{ font: `400 12.5px/17px ${T.fontSans}`, color: T.inkSoft, width: 90, flexShrink: 0, textAlign: 'right', fontStyle: w.old ? 'normal' : 'italic' }}>{w.old || 'blank'}</span>
                                  <i className="fa-solid fa-arrow-right" style={{ fontSize: 9, color: T.inkSoft }} />
                                  <span style={{ font: `600 13px/18px ${T.fontSans}`, color: T.ink, minWidth: 90 }}>{w.value}</span>
                                  {w.edited ? <Badge color="blueLight" icon="pencil">Edited</Badge> : <ConfTag conf={w.confidence} />}
                                  <span style={{ marginLeft: 'auto', font: `400 11px/15px ${T.fontSans}`, color: T.inkSoft, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 170 }}>
                                    {w.sourceFilename && <Fragment><i className="fa-solid fa-paperclip" style={{ fontSize: 9, marginRight: 4 }} />{w.sourceFilename}</Fragment>}
                                  </span>
                                </div>
                              ))}
                            </div>
                            {isRev && (
                              <div style={{ font: `400 12px/16px ${T.fontSans}`, color: T.inkMuted, marginTop: 7 }}>
                                <i className="fa-solid fa-rotate-left" style={{ fontSize: 10, marginRight: 5 }} />
                                Reverted — previous values restored in FMX. The scan results remain here for reference.
                              </div>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                  </Fragment>
                );
              })}
              {rows.length === 0 && (
                <tr><td colSpan={8} style={{ ...cs, textAlign: 'center', padding: '28px 14px', color: T.inkSoft }}>
                  {writes.length ? 'No applied records match these filters.' : 'Nothing applied yet — approved values from the review desk land here.'}
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
